import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Sparkles } from "lucide-react";

export default function LoyaltyBuy() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const tierId = params.get("tier") || "strip-starter";
  const [tier, setTier] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/loyalty/tiers").then(({ data }) => {
      const found = (data?.tiers || []).find((x) => x.id === tierId) || (data?.tiers || [])[0];
      setTier(found || null);
    }).catch(() => {});
  }, [tierId]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/loyalty/purchase", {
        ...form,
        tier_id: tierId,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        toast.success("Redirecting to secure payment…");
        window.location.href = data.checkout_url;
        return;
      }
      toast.error("Could not start payment. Please try again.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="loyalty-buy-page"
      className="max-w-2xl mx-auto px-4 pt-12 md:pt-20 pb-24"
    >
      <div className="text-center">
        <div className="text-[10px] uppercase tracking-[0.32em] text-neon font-bold">
          {t("loyalty.kicker")}
        </div>
        <h1 className="font-heading text-4xl md:text-6xl uppercase mt-3 leading-[0.95]">
          <span className="stroke-pink">{tier?.name || "Drinks Ticket"}</span>
        </h1>
        <p className="text-white/70 mt-4 text-sm md:text-base" data-testid="loyalty-tier-tagline">
          {tier?.tagline || t("loyalty.tagline")}
        </p>

        <div
          className="mt-6 mx-auto inline-flex flex-wrap items-center justify-center gap-3 px-5 py-3 rounded-full"
          style={{
            background: "linear-gradient(135deg, rgba(255,27,141,0.15), rgba(255,107,198,0.05))",
            border: "1.5px solid rgba(255,27,141,0.55)",
            boxShadow: "0 0 18px rgba(255,27,141,0.35)",
          }}
        >
          <Sparkles size={16} className="text-neon" />
          <span className="font-heading text-2xl md:text-3xl text-white" data-testid="loyalty-tier-price">
            €{tier?.price_eur ?? ""}
          </span>
          <span className="text-xs uppercase tracking-[0.28em] text-white/70" data-testid="loyalty-tier-includes">
            {tier?.tagline || ""}
          </span>
        </div>

        <div data-testid="loyalty-deposit-breakdown"
             className="mt-4 max-w-md mx-auto grid grid-cols-1 gap-2 text-left">
          <div className="rounded-2xl border border-neon/30 bg-black/40 px-4 py-3 text-center">
            <div className="text-[10px] uppercase tracking-[0.28em] text-neon font-bold">Pay in Full</div>
            <div className="font-heading text-2xl md:text-3xl text-neon mt-1">€{tier?.price_eur ?? ""}</div>
          </div>
        </div>
        <p className="mt-3 text-[10px] uppercase tracking-[0.28em] text-white/45 text-center">
          Paid in Full · Zero balance due at the venue.
        </p>
      </div>

      <form
        onSubmit={submit}
        className="surface rounded-4xl mt-10 p-6 md:p-8 space-y-5"
      >
        <Field label={t("loyalty.field.name")}>
          <Input
            data-testid="loyalty-input-name"
            required
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder={t("loyalty.placeholder.name")}
          />
        </Field>
        <Field label={t("loyalty.field.phone")}>
          <Input
            data-testid="loyalty-input-phone"
            required
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder="+34 600 000 000"
          />
        </Field>
        <Field label={t("loyalty.field.email")}>
          <Input
            data-testid="loyalty-input-email"
            required
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="tu@email.com"
          />
          <p className="text-[10px] uppercase tracking-[0.22em] text-neon mt-1.5 font-bold">
            📧 Required · your card link + proof of purchase
          </p>
        </Field>

        <button
          type="submit"
          data-testid="loyalty-submit"
          disabled={submitting || !tier}
          className="btn-pink w-full"
        >
          {submitting
            ? t("loyalty.submitting")
            : `Pay €${tier?.price_eur ?? ""} ${t("loyalty.submit")}`}
        </button>

        <p className="text-[10px] uppercase tracking-[0.28em] text-white/40 text-center">
          {t("loyalty.terms")}
        </p>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.32em] text-neon font-bold">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
function Input(props) {
  return (
    <input
      {...props}
      className="w-full rounded-2xl bg-black/40 border border-white/10 px-4 py-3 focus:border-neon focus:outline-none focus:ring-1 focus:ring-neon text-white placeholder:text-white/30"
    />
  );
}
